import Script from 'next/script';
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';

declare global {
  interface Window {
    fbq: any;
  }
}

export enum EVENTS {
  PageView = 'PageView',
  Purchase = 'Purchase',
  InitiateCheckout = 'InitiateCheckout'
}

type MetaPixelProps = {
  pixelId?: string;
  initEventId?: EVENTS;
  initEventData?: any;
};

export interface MetaPixelRef {
  trackCheckoutSuccess: (data?: {
    eventName: string;
    numberOfTickets: number;
    currency: string;
    value: number;
  }) => void;
  trackCheckoutInitiated: (data?: { eventName: string }) => void;
}

const EVENTS_HANDLERS = {
  [EVENTS.PageView]: (pixelId: string) => {
    window.fbq('trackSingle', pixelId, 'PageView');
  },
  [EVENTS.Purchase]: (
    pixelId: string,
    data: {
      eventName: string;
      numberOfTickets: number;
      currency: string;
      value: number;
    }
  ) => {
    window.fbq('trackSingle', pixelId, 'Purchase', {
      content_category: data.eventName,
      num_items: data.numberOfTickets,
      currency: data.currency,
      value: data.value
    });
  },
  [EVENTS.InitiateCheckout]: (
    pixelId: string,
    data: {
      eventName: string;
    }
  ) => {
    window.fbq('trackSingle', pixelId, 'InitiateCheckout', {
      content_category: data.eventName
    });
  }
};

const MetaPixel = forwardRef<MetaPixelRef, MetaPixelProps>(
  (
    { pixelId = undefined, initEventId = undefined, initEventData = undefined },
    ref
  ) => {
    const [loaded, setLoaded] = useState(
      Boolean(typeof window !== 'undefined' && !!window.fbq)
    );

    useImperativeHandle(ref, () => ({
      trackCheckoutSuccess: (data) => {
        if (window.fbq && data) {
          if (window.fbq?.instance?.pixelsByID[pixelId]) {
            EVENTS_HANDLERS[EVENTS.Purchase](pixelId, data);
          }

          // Also send the event to the BOUNCE Meta Pixel.
          EVENTS_HANDLERS[EVENTS.Purchase](
            process.env.NEXT_PUBLIC_META_PIXEL_ID,
            data
          );
        }
      },
      trackCheckoutInitiated: (data) => {
        if (window.fbq && data) {
          if (window.fbq?.instance?.pixelsByID[pixelId]) {
            EVENTS_HANDLERS[EVENTS.InitiateCheckout](pixelId, data);
          }

          // Also send the event to the BOUNCE Meta Pixel.
          EVENTS_HANDLERS[EVENTS.InitiateCheckout](
            process.env.NEXT_PUBLIC_META_PIXEL_ID,
            data
          );
        }
      }
    }));

    useEffect(() => {
      if (!loaded) {
        return;
      }

      // Initialize the BOUNCE Meta Pixel if it hasn't been initialized yet. And send a PageView event.
      if (
        !window.fbq?.instance?.pixelsByID[process.env.NEXT_PUBLIC_META_PIXEL_ID]
      ) {
        window.fbq('init', process.env.NEXT_PUBLIC_META_PIXEL_ID);
        window.fbq(
          'set',
          'autoConfig',
          false,
          process.env.NEXT_PUBLIC_META_PIXEL_ID
        );
        EVENTS_HANDLERS[EVENTS.PageView](process.env.NEXT_PUBLIC_META_PIXEL_ID);
      }

      // Initialize the CUSTOM Meta Pixel ID if it hasn't been initialized yet.
      if (
        pixelId &&
        !window.fbq?.instance?.pixelsByID[pixelId] &&
        pixelId !== process.env.NEXT_PUBLIC_META_PIXEL_ID
      ) {
        window.fbq.disablePushState = true;
        window.fbq('set', 'autoConfig', false, pixelId);
        window.fbq('init', pixelId);
      }

      // Send the passed INIT event for the passed custom Meta Pixel ID.
      // If the event is NOT a PageView event, also send it to the BOUNCE Meta Pixel.
      if (initEventId && pixelId) {
        EVENTS_HANDLERS[initEventId](pixelId, initEventData);

        if (initEventId !== EVENTS.PageView) {
          EVENTS_HANDLERS[initEventId](
            process.env.NEXT_PUBLIC_META_PIXEL_ID,
            initEventData
          );
        }
      }
    }, [loaded]);

    // TODO: If we make this evolve to fire other events, it will make more sense to change this implementation.
    // Move the script loading to a context provider (probably just wrapping the event page).
    // The attached context should provide the required methods to fire events for a specific pixel.
    // All parts of the event and subcomponents can now access and fire Meta related events.
    return (
      <Script
        id="fb-pixel"
        src="/meta-pixel.js"
        strategy="afterInteractive"
        onLoad={() => setLoaded(true)}
      />
    );
  }
);

export default MetaPixel;
